import axios from "axios";
import { mockRequestHandler } from "../../__mocks__/mockRequestHandler"; // Import the mock request handler
import { redirectToAuthPortal } from "./authPortal";

/**
 * No-op retained for backwards compatibility while the in-app login surface is
 * being removed. Token caching no longer exists — the FE holds no tokens; the
 * `.foo.com` session cookie authenticates every request.
 * @deprecated remove once the last caller (sagas/signin.js) is deleted.
 */
export const clearCachedToken = () => {};

const useMockedRoutes = process.env.USE_MOCKED_ROUTES === "true";
class SecureRequest {
  constructor() {
    // Auth is carried entirely by the `.foo.com` session cookie
    // (withCredentials). an-api derives user_id from the session and takes
    // client_id as a query param, so no identity headers are sent.
    this.request = axios.create({
      headers: {
        "Content-Type": "application/json",
      },
      baseURL: process.env.BASE_URL,
      withCredentials: true,
    });

    // Session loss is detected reactively: any 401 means the cookie session is
    // gone/expired, so we send the user to the auth portal to re-authenticate.
    this.request.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          redirectToAuthPortal();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Returns the appropriate request handler based on the environment.
   * @returns {AxiosInstance | {
   *   get: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse>,
   *   post: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse>,
   *   put: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse>,
   *   delete: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse>,
   *   patch: (url: string, data?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse>
   * }}
   */
  getRequest() {
    if (useMockedRoutes) {
      return {
        get: (url, config) => this.handleRequestWithFallback(url, "GET", config),
        post: (url, data, config) =>
          this.handleRequestWithFallback(url, "POST", { ...config, data }),
        put: (url, data, config) => this.handleRequestWithFallback(url, "PUT", { ...config, data }),
        delete: (url, config) => this.handleRequestWithFallback(url, "DELETE", config),
        patch: (url, data, config) =>
          this.handleRequestWithFallback(url, "PATCH", { ...config, data }),
      };
    }
    return this.request; // Use Axios instance for live API requests
  }

  /**
   * Handles requests in debug mode with fallback to live API calls.
   * @param {string} url - The API endpoint.
   * @param {string} method - The HTTP method (GET, POST, PUT, DELETE, PATCH).
   * @param {object} options - Request options (e.g., params, data, headers).
   * @returns {Promise<{status: number, data: object}>} - The resolved response (mock or live API).
   */
  async handleRequestWithFallback(url, method, options = {}) {
    try {
      const requestData = {
        query: options?.params,
        body: options?.data,
      };
      const { status, response } = mockRequestHandler.resolve(url, method, requestData);

      return Promise.resolve({ status, data: response });
    } catch {
      console.warn(`Mock not found or inactive for ${method} ${url}. Falling back to live API.`);
      // Fallback to the actual Axios instance for live API call. Body methods
      // take (url, data, config); GET/DELETE take (url, config).
      if (["POST", "PUT", "PATCH"].includes(method)) {
        const { data, ...config } = options;
        return this.request[method.toLowerCase()](url, data, config);
      }
      return this.request[method.toLowerCase()](url, options);
    }
  }
}

export default SecureRequest;

// helpers

/** @type {(url: string, config?: import('axios').AxiosRequestConfig) => import('axios').AxiosResponse} */
export function apiGet(...args) {
  return new SecureRequest().getRequest().get(...args);
}

/** @type {(url: string, config?: import('axios').AxiosRequestConfig) => import('axios').AxiosResponse} */
export function apiDelete(...args) {
  return new SecureRequest().getRequest().delete(...args);
}

/** @type {(url: string, data?: any, config?: import('axios').AxiosRequestConfig) => import('axios').AxiosResponse} */
export function apiPut(...args) {
  return new SecureRequest().getRequest().put(...args);
}

/** @type {(url: string, data?: any, config?: import('axios').AxiosRequestConfig) => import('axios').AxiosResponse} */
export function apiPost(...args) {
  return new SecureRequest().getRequest().post(...args);
}

/** @type {(url: string, data?: any, config?: import('axios').AxiosRequestConfig) => import('axios').AxiosResponse} */
export function apiPatch(...args) {
  return new SecureRequest().getRequest().patch(...args);
}
